import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { PanAppState, USER_ACTIONS, UserState } from '@panamax/app-state';
import { User } from '@usf/user-types/user';
import { combineLatest, of } from 'rxjs';
import {
  catchError,
  concatMap,
  exhaustMap,
  first,
  map,
  tap,
} from 'rxjs/operators';
import { UserDataService } from '../../services/user-data.service';
import { UserActions } from '../actions/action-types';
import { UserService } from '@app/user/services';
import { LoadingSpinnerService } from '@shared/services/loading-spinner/loading-spinner.service';
import { ManageUsersService } from '@app/manage-users/services/manage-users.service';
import { UserPreferencesV2 } from '@usf/user-types/user-preferencev2';
import { TranslateService } from '@ngx-translate/core';
import { MessageTypeEnum } from '../../../ngrx-message/constants/messageTypeEnum';
import { MessageStoreService } from '@app/ngrx-message/services/message/message-store.service';
import { Message } from '@app/ngrx-message/models/client/message';
import { ManageUsersAnalyticsService } from '@app/manage-users/tracking/analytics/manage-users-analytics.service';

@Injectable({
  providedIn: 'root',
})
export class UserEffects {
  getUserbyToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getUserByToken),
      exhaustMap(action =>
        combineLatest([
          this.userDataService.getByToken(),
          this.userDataService.getUserProfiles(),
        ]).pipe(
          first(
            ([userPayload, profilesPayload]) =>
              !!userPayload && !!profilesPayload,
          ),
          concatMap(([userPayload, profilesPayload]) => [
            UserActions.getUserByTokenSuccess({
              user: userPayload,
              profiles: profilesPayload,
            }),
            UserActions.getUserPreferences(),
            UserActions.getUserCustomization(),
            USER_ACTIONS.loadUserSuccess({
              loggedInUser: this.createPanamaxUser(
                userPayload,
                action?.impersonatorID,
              ),
            }),
            USER_ACTIONS.loadProfileSettings({
              profileSettings: profilesPayload,
            }),
          ]),
          catchError(err => of(UserActions.getUserByTokenFail({ error: err }))),
        ),
      ),
    ),
  );

  getUserCustomizations$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getUserCustomization),
      concatMap(action => {
        return this.userDataService.getUserCustomizations().pipe(
          map(response =>
            UserActions.getUserCustomizationSuccess({
              customization: response[0],
            }),
          ),
          tap(response => {
            console.log('CUSTOMIZATIONS', response);
            this.panAppState.setCustomization(
              response?.customization?.profileName ?? null,
            );
          }),
          catchError((error: HttpErrorResponse) => {
            this.panAppState.setCustomization(null);
            return of(UserActions.getUserCustomizationFail({ error }));
          }),
        );
      }),
    ),
  );

  getAllSiteCustomizationProfiles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getAllSiteCustomizationProfiles),
      concatMap(action => {
        return this.userDataService.getAllSiteCustomizationProfiles().pipe(
          map(response =>
            UserActions.getAllSiteCustomizationProfilesSuccess({
              customizations: response,
            }),
          ),
          catchError((error: HttpErrorResponse) => {
            this.panAppState.setCustomization(null);
            return of(
              UserActions.getAllSiteCustomizationProfilesFail({ error }),
            );
          }),
        );
      }),
    ),
  );

  getUserPreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getUserPreferences),
      concatMap(action => {
        return this.userDataService.getUserPreferences().pipe(
          map(response =>
            UserActions.getUserPreferencesSuccess({ preferences: response }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(UserActions.getUserPreferencesFail({ error })),
          ),
        );
      }),
    ),
  );

  updateUserPreferences$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateUserPreferences),
      concatMap(action => {
        return this.userDataService
          .updateUserPreferences(action.newPreferences)
          .pipe(
            map(response => UserActions.updateUserPreferencesSuccess()),
            catchError((error: HttpErrorResponse) =>
              of(
                UserActions.updateUserPreferencesFail({
                  error,
                  oldPreferences: action.oldPreferences,
                }),
              ),
            ),
          );
      }),
    ),
  );

  getEmailPhoneByUsername$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getEmailPhoneByUsername),
      concatMap(action => {
        return this.userDataService
          .getEmailPhoneByUsername(action.username)
          .pipe(
            map(response =>
              UserActions.getEmailPhoneByUsernameSuccess({
                emailAndPhone: response,
              }),
            ),
            catchError((error: HttpErrorResponse) =>
              of(UserActions.getEmailPhoneByUsernameFail({ error })),
            ),
          );
      }),
    ),
  );

  getInteractBanners$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getInteractBanners),
      concatMap(action => {
        return this.userDataService.getInteractBanners().pipe(
          map(response =>
            UserActions.getInteractBannersSuccess({
              interactBanners: response,
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(UserActions.getInteractBannersFail({ error })),
          ),
        );
      }),
    ),
  );

  getCustomMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getCustomMessage),
      concatMap(action => {
        return this.userDataService.getCustomMessage().pipe(
          map(response =>
            UserActions.getCustomMessageSuccess({
              customMessage: response,
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(UserActions.getCustomMessageFail({ error })),
          ),
        );
      }),
    ),
  );

  getCustomMessages$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getCustomMessages),
      concatMap(action => {
        return this.userDataService.getCustomMessages().pipe(
          map(response =>
            UserActions.getCustomMessagesSuccess({
              customMessages: response,
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(UserActions.getCustomMessagesFail({ error })),
          ),
        );
      }),
    ),
  );

  saveCustomMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.saveCustomMessage),
      concatMap(action => {
        return this.userDataService
          .saveCustomMessage(action.customMessage)
          .pipe(
            map(response =>
              UserActions.saveCustomMessageSuccess({
                customMessage: action.customMessage,
              }),
            ),
            catchError((error: HttpErrorResponse) =>
              of(UserActions.saveCustomMessageFail({ error })),
            ),
          );
      }),
    ),
  );

  deleteCustomMessage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.deleteCustomMessage),
      concatMap(action => {
        return this.userDataService.deleteCustomMessage(action.messageIds).pipe(
          map(response =>
            UserActions.deleteCustomMessageSuccess({
              messageIds: action.messageIds,
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(UserActions.deleteCustomMessageFail({ error })),
          ),
        );
      }),
    ),
  );

  inviteNewUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.inviteNewUser),
      concatMap(action => {
        return this.userDataService
          .inviteNewUser(
            action.inviteEmail,
            action.customers,
            action.fullUserProfile,
            action.organizationName,
          )
          .pipe(
            map(response =>
              UserActions.inviteNewUserSuccess({
                inviteEmail: action.inviteEmail,
              }),
            ),
            catchError((error: HttpErrorResponse) =>
              of(
                UserActions.inviteNewUserFail({
                  error,
                  inviteEmail: action.inviteEmail,
                }),
              ),
            ),
          );
      }),
    ),
  );

  searchUserByUsername$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.searchUserByUsername),
      concatMap(action => {
        return this.userDataService.searchUserByUsername(action.userName).pipe(
          map((response: User) =>
            UserActions.searchUserByUsernameSuccess({
              user: response,
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              UserActions.searchUserByUsernameFail({
                error,
              }),
            ),
          ),
        );
      }),
    ),
  );

  getUserPreferencesV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getUserPreferencesV2),
      concatMap(payload =>
        this.userDataService.getUserPreferencesV2(payload?.userId).pipe(
          map(response => {
            const preferences: UserPreferencesV2 = {
              userId: payload?.userId,
              favoriteCustomers: response?.favoriteCustomers,
              ...(response?.orderStatusFilters && {
                orderStatusFilters: response.orderStatusFilters,
              }),
              ...(response?.poNumber && {
                poNumber: Array.isArray(response.poNumber)
                  ? response.poNumber
                  : [],
              }),
            };
            return UserActions.getUserPreferencesV2Success({
              preferences,
            });
          }),
          catchError((error: HttpErrorResponse) =>
            of(UserActions.getUserPreferencesV2Fail({ error })),
          ),
        ),
      ),
    ),
  );

  updateUserPreferencesV2$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateUserPreferencesV2),
      concatMap(({ preferences }) =>
        this.userDataService.updateUserPreferencesV2(preferences).pipe(
          map(updatedPreferences =>
            UserActions.updateUserPreferencesV2Success({
              preferences: updatedPreferences,
            }),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              UserActions.updateUserPreferencesV2Fail({
                error,
                oldPreferences: preferences,
              }),
            ),
          ),
        ),
      ),
    ),
  );

  getCustomerUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getCustomerUsers),
      concatMap(action =>
        this.userDataService.getCustomerUsers().pipe(
          map(customerUsers => {
            return UserActions.getCustomerUsersSuccess({
              customerUsers,
            });
          }),
          catchError((error: HttpErrorResponse) => {
            this.loadingSpinnerService.dismissSpinnerModal();
            return of(
              UserActions.getCustomerUsersFail({
                error,
              }),
            );
          }),
        ),
      ),
    ),
  );

  getCustomerUsersSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.getCustomerUsersSuccess),
        tap(payload => {
          this.manageUsersService.setCustomerUsersCallComplete(true);
          this.manageUsersService.setCustomerUsers(payload?.customerUsers);
          this.loadingSpinnerService.dismissSpinnerModal();
        }),
      ),
    { dispatch: false },
  );

  getCustomerUsersFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.getCustomerUsersFail),
        tap(action => {
          this.manageUsersService.setCustomerUsersCallComplete(true);
          this.manageUsersService.setCustomerUsers([]);
          this.displayToastMessage(
            MessageTypeEnum.error,
            this.translateService.instant('i18n.manageUsers.errorMessage'),
          );
        }),
      ),
    { dispatch: false },
  );

  removeCustomerUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.removeCustomerUser),
      concatMap(action =>
        this.userDataService
          .updateCustomerUser(action?.customerUserUpdate)
          .pipe(
            map(response => {
              return UserActions.removeCustomerUserSuccess();
            }),
            catchError((error: HttpErrorResponse) => {
              this.loadingSpinnerService.dismissSpinnerModal();
              return of(UserActions.removeCustomerUserFail({ error }));
            }),
          ),
      ),
    ),
  );

  removeCustomerUserSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.removeCustomerUserSuccess),
        tap(action => {
          this.manageUsersService.loadAllUsersForCustomer();
          this.displayToastMessage(
            MessageTypeEnum.success,
            this.translateService.instant('i18n.manageUsers.userRemoved'),
          );

          this.manageUsersAnalyticsService.trackRemoveUser();
        }),
      ),
    { dispatch: false },
  );

  removeCustomerUserFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.removeCustomerUserFail),
        tap(action => {
          this.displayToastMessage(
            MessageTypeEnum.error,
            this.translateService.instant('i18n.manageUsers.errorMessage'),
          );
        }),
      ),
    { dispatch: false },
  );

  getClientConcepts$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.getClientConcepts),
      concatMap(action => {
        return this.userDataService.getClientConcepts().pipe(
          map(response =>
            UserActions.getClientConceptsSuccess({
              clientConcepts: response,
            }),
          ),
          catchError((error: HttpErrorResponse) => {
            return of(UserActions.getClientConceptsFail({ error }));
          }),
        );
      }),
    ),
  );

  updateCustomerUserAdmin$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserActions.updateCustomerUserAdmin),
      concatMap(action =>
        this.userDataService
          .updateCustomerUser(action?.customerUserUpdate)
          .pipe(
            map(response => {
              return UserActions.updateCustomerUserAdminSuccess({
                customerUserUpdate: action?.customerUserUpdate,
              });
            }),
            catchError((error: HttpErrorResponse) => {
              this.loadingSpinnerService.dismissSpinnerModal();
              return of(UserActions.updateCustomerUserAdminFail({ error }));
            }),
          ),
      ),
    ),
  );

  updateCustomerUserAdminSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.updateCustomerUserAdminSuccess),
        tap(action => {
          this.manageUsersService.loadAllUsersForCustomer();
          const translation = action.customerUserUpdate.customerAdmin
            ? 'i18n.manageUsers.userAddedAdmin'
            : 'i18n.manageUsers.userRemovedAdmin';
          this.displayToastMessage(
            MessageTypeEnum.success,
            this.translateService.instant(translation),
          );

          // if customerAdmin is true, user was made admin, else user was removed from admin
          if (action.customerUserUpdate.customerAdmin) {
            this.manageUsersAnalyticsService.trackMakeAdmin();
          } else {
            this.manageUsersAnalyticsService.trackRemoveAdmin();
          }
        }),
      ),
    { dispatch: false },
  );

  updateCustomerUserAdminFail$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(UserActions.updateCustomerUserAdminFail),
        tap(action => {
          this.displayToastMessage(
            MessageTypeEnum.error,
            this.translateService.instant('i18n.manageUsers.errorMessage'),
          );
        }),
      ),
    { dispatch: false },
  );

  displayToastMessage(type: MessageTypeEnum, message: string): void {
    this.messageStoreService.upsertMessage({
      type: type,
      display: message,
      toast: true,
      sticky: false,
    } as Message);
  }

  constructor(
    private actions$: Actions,
    private userDataService: UserDataService,
    private userService: UserService,
    private manageUsersService: ManageUsersService,
    private panAppState: PanAppState,
    private loadingSpinnerService: LoadingSpinnerService,
    private translateService: TranslateService,
    private messageStoreService: MessageStoreService,
    private manageUsersAnalyticsService: ManageUsersAnalyticsService,
  ) {}

  createPanamaxUser(user: User, impersonation?: string): UserState {
    const statObj = {
      organizationName: user.organizationName,
      userId: user.userName.replace('[AD_AUTO]', ''),
      ecomUserId: user.userId,
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      userStatus: user.userStatus,
      lastLoginDtm: user.lastLoginDtm,
      userType: user.userType,
      partnerId: user.partnerId,
      userKind: user.userKind,
      impersonatorID: '',
      addDtm: user.addDtm,
    };
    !!impersonation
      ? (statObj['impersonatorID'] = impersonation)
      : (statObj['impersonatorID'] = '');
    return statObj;
  }
}
